import 'react-toastify/dist/ReactToastify.min.css';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slide, toast, ToastContainer as ToastContainerT } from 'react-toastify';

import { useI18n } from 'hooks/useI18n';
import PropTypes from 'prop-types';
import { toast as toastSlice } from 'store/toast';
import styled from 'styled-components';

import { Box } from 'components/@ux/box';

// BASICS
const { POSITION, TYPE } = toast;

const defaultOptions = {
  type: TYPE.SUCCESS,
  position: POSITION.TOP_RIGHT,
  autoClose: 20000,
  pauseOnHover: false,
  pauseOnFocusLoss: false,
  closeButton: false,
};

// BODIES
const SimpleBody = ({ title, message }) => (
  <Box data-testid="ToastBody">
    {title && (
      <Box textAlign="center" fontSize="1.4rem" fontWeight="bold" m="1rem 0">
        {title}
      </Box>
    )}
    {message && (
      <Box pb="1rem" textAlign="center" fontSize="lg">
        {message}
      </Box>
    )}
  </Box>
);

SimpleBody.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

// PROVIDER
const StyledToastContainer = styled(ToastContainerT)`
  min-width: 400px;
  @media (max-width: 600px) {
    min-width: 0;
  }
  .Toastify__toast--success {
    background-color: rgba(0, 100, 0, 0.7);
  }
  .Toastify__toast--error {
    background-color: rgba(231, 76, 60, 0.7);
  }
`;

const thToShowDismissAll = 2;

export const ToastContainer = () => {
  const [amount, setAmount] = useState(0);

  const [idDismissAll, setIdDismissAll] = useState(null);
  const [isVisibleDismissAll, setIsVisibleDismissAll] = useState(false);

  const dispatch = useDispatch();
  const t = useI18n();
  const messages = useSelector(toastSlice.selectors.getState);
  const hasMessages = !!messages.length;

  const handleRemoveFirst = () => dispatch(toastSlice.actions.removeFirst());
  const handleDismissAll = () => toast.dismiss();

  const handleShowToast = ({ options, title, titleArgs, message, messageArgs }) => {
    toast(
      <SimpleBody
        title={title || (titleArgs && t(...titleArgs))}
        message={message || (messageArgs && t(...messageArgs))}
      />,
      {
        ...defaultOptions,
        ...options,
        onClose: () => setAmount((a) => a - 1),
        onOpen: () => setAmount((a) => a + 1),
      }
    );
  };

  const handleMountDismissAll = () =>
    setIdDismissAll(
      toast(
        <Box height="2rem" centeredContent>
          {t('dismiss_all')}
        </Box>,
        {
          ...defaultOptions,
          hideProgressBar: true,
          style: { display: 'none' },
          onClick: handleDismissAll,
        }
      )
    );

  const handleShowDismissAll = () => {
    setIsVisibleDismissAll(true);

    toast.update(idDismissAll, { style: { display: 'block' } });
  };

  const handleRefreshDismissAll = handleShowDismissAll;

  const handleHideDismissAll = () => {
    setIsVisibleDismissAll(false);

    toast.update(idDismissAll, { style: { display: 'none' } });
  };

  useEffect(() => {
    if (!amount && hasMessages) handleMountDismissAll();
  }, [hasMessages, amount]);

  useEffect(() => {
    if (amount && amount < thToShowDismissAll && isVisibleDismissAll) handleHideDismissAll();

    if (amount > thToShowDismissAll && !isVisibleDismissAll) handleShowDismissAll();
  }, [amount]);

  useEffect(() => {
    if (hasMessages) {
      if (isVisibleDismissAll) handleRefreshDismissAll();
      handleShowToast(messages[0]);
      handleRemoveFirst();
    }
  }, [hasMessages]);

  return <StyledToastContainer data-testid="ToastContainer" transition={Slide} />;
};
